import { CombinedGraphQLErrors, CombinedProtocolErrors } from '@apollo/client/errors';
import type { ErrorLink } from '@apollo/client/link/error';
import { loginPath } from '@components/modules/auth/urls';
import { notify } from '@components/ui/molecules/Toast';

import { isPublicPath } from './publicPaths';
import { useCookiesManager } from './useCookiesManager';

let unauthorized = false;
const paymentMethodUnavailableRegex =
  /no provider available:\s*no active provider found for card type/i;

const isPaymentMethodUnavailableError = (message?: string) =>
  Boolean(message && paymentMethodUnavailableRegex.test(message));

// For graphql
export const graphqlErrorHandler = ({ error }: ErrorLink.ErrorHandlerOptions) => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ message, extensions }) => {
      if (extensions?.code === 'JWT_TOKEN_EXPIRED') {
        handleUnauthorizedError();
      } else if (isPaymentMethodUnavailableError(message)) {
        // This is a known, user-correctable state shown inline in the payment UI.
      } else {
        // Show toast for other GraphQL errors
        notify.error({
          title: 'GraphQL Error',
          description: message || 'An error occurred while processing your request',
        });
      }
    });
  } else if (CombinedProtocolErrors.is(error)) {
    error.errors.forEach(({ message, extensions }) => {
      console.error(
        `[Protocol error]: Message: ${message}, Extensions: ${JSON.stringify(extensions)}`
      );
      notify.error({
        title: 'Protocol Error',
        description: message || 'A protocol error occurred',
      });
    });
  } else {
    // Network error
    console.error(`[Network error]: ${error}`);
    notify.error({
      title: 'Network Error',
      description: 'Unable to connect to the server. Please check your internet connection.',
    });
  }
};

const handleUnauthorizedError = () => {
  if (unauthorized) return;

  unauthorized = true;

  // Drop the rejected session before leaving the page. Redirecting with the
  // stale cookies still set makes the next page load fail the same way and
  // redirect again — an infinite reload loop.
  useCookiesManager.clearAll();

  // Public pages (accept-invitation, payment links, ...) work without a
  // session; a background query failing auth there must not hijack the page.
  if (!isPublicPath(window.location.pathname)) {
    window.location.href = loginPath;
  }
};
